import { ClockCircleOutlined } from "@ant-design/icons";
import { Col, Input, Row } from "antd";
import { useVerifyUserEmailMutation } from "api/User/hooks.ts";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import { RegistrationSuccess } from "components/UI/RegistrationSuccess/RegistrationSuccess.tsx";
import { TenantAuthFormWrapper } from "components/Wrappers/TenantAuthFormWrapper/TenantAuthFormWrapper.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { useUser } from "hooks/useUser.ts";
import { FC, useState } from "react";
import { FormItem } from "react-hook-form-antd";
import { toast } from "react-toastify";
import { RoleEnum } from "types/enums.ts";

import { ActivateFormData, schema } from "./formSchema";

import GetUserResponse = App.Http.Responses.User.GetUserResponse;

interface ActivateFormProps {
    userId: string;
    emailHash: string;
    tenantSlug?: string;
}

interface ActivateFormContentProps {
    isSubmitting?: boolean;
}

const ActivateFormContent: FC<ActivateFormContentProps> = ({ isSubmitting }) => {
    const { t } = usePaldockTranslation("forms.activate");
    const { t: tFields } = usePaldockTranslation("forms.general-fields");
    const { control, fieldNames } = useGeneralForm<ActivateFormData>();

    return (
        <TenantAuthFormWrapper
            buttonText={t("actions.activate_account")}
            loading={isSubmitting}
            buttonDisabled={isSubmitting}
        >
            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <FormItem
                        control={control}
                        name={fieldNames.password.name}
                        label={tFields("password.label")}
                        className="!mb-0"
                    >
                        <Input.Password placeholder={tFields("password.placeholder")} />
                    </FormItem>
                </Col>
                <Col span={24}>
                    <FormItem
                        control={control}
                        name={fieldNames.password_confirmation.name}
                        label={tFields("password_confirmation.label")}
                        className="!mb-0"
                    >
                        <Input.Password placeholder={tFields("password_confirmation.placeholder")} />
                    </FormItem>
                </Col>
            </Row>
        </TenantAuthFormWrapper>
    );
};

export const ActivateForm: FC<ActivateFormProps> = ({ userId, emailHash, tenantSlug }) => {
    const url = new URL(window.location.href);
    const expires = url.searchParams.get("expires");
    const signature = url.searchParams.get("signature");
    // tenant_id/role are part of the signed URL's signature (see getTemplatedTemporarySignedRoute on the
    // backend) — they must be forwarded exactly as issued, or the link is treated as invalid/tampered.
    const tenantId = url.searchParams.get("tenant_id") ?? undefined;
    const role = (url.searchParams.get("role") as RoleEnum | null) ?? undefined;
    const userUtils = useUser();
    const { t: tAuth } = usePaldockTranslation("auth.activate");
    const [pendingApproval, setPendingApproval] = useState(false);

    const verifyEmailMutation = useVerifyUserEmailMutation({
        onSuccess: (response: GetUserResponse) => {
            // A closed (non-open_registration) tenant join only creates a pending UserRequest —
            // the user never gets an active tenant_user row, so navigating straight to the tenant
            // would just bounce through the dashboard guard to /no-access. Show the pending-approval
            // screen instead, the same way the existing-user confirm-registration flow does.
            const hasActiveTenantAccess =
                !tenantId ||
                response.user.tenants?.some((tenant) => tenant.id === tenantId && tenant.user_active !== false);

            if (!hasActiveTenantAccess) {
                setPendingApproval(true);
                return;
            }

            userUtils.loginUser(response.token || "", response.user, tenantSlug);
        },
        onError: (errorResponse) => {
            toast.error(`Error: ${errorResponse.message}`);
        },
    });

    const onSubmit = (data: ActivateFormData) => {
        verifyEmailMutation.mutate({
            id: userId,
            hash: emailHash,
            expires,
            signature,
            tenant_id: tenantId,
            role,
            password: data.password,
            password_confirmation: data.password_confirmation,
        });
    };

    if (pendingApproval) {
        return (
            <RegistrationSuccess
                message={tAuth("messages.pending_approval_message")}
                icon={<ClockCircleOutlined style={{ fontSize: "167px", color: "#FFA940" }} />}
                alertType="warning"
            />
        );
    }

    return (
        <GeneralFormProvider<ActivateFormData>
            schema={schema}
            defaultValues={{ password: "", password_confirmation: "" }}
            onSubmit={onSubmit}
            requiredMark={false}
        >
            <ActivateFormContent isSubmitting={verifyEmailMutation.isPending} />
        </GeneralFormProvider>
    );
};
